// ─────────────────────────────────────────────
//  useChannelMessages — opens ONE real-time listener per derived channel id and
//  keeps a { channelId → messages[] } map in state. Mirrors useConversations'
//  multi-listener + client-merge + cleanup shape (MessagesPage), generalized to
//  N channels (D-02: one listener per channel, never per message/contact).
//
//  STABILITY (ATC test-mock gotcha / Pitfall T-05-11): `channelIds` is almost
//  always a fresh array each render (e.g. channels.map(c => c.id)). Putting a
//  fresh array directly in the effect deps re-subscribes every render and can
//  OOM-crash the vitest worker. We memoize on a STABLE join key so the effect
//  only re-runs when the actual set of ids changes.
//
//  A DEAD CHANNEL AND A QUIET ONE ARE THE SAME SHAPE. The retry below is
//  bounded, and when it runs out the listener is simply gone — but the map
//  still holds `{ [channelId]: [] }`, which is byte-for-byte what a channel
//  nobody has posted in looks like. Every surface downstream then draws its
//  calm "No messages" empty state for a bus the app has stopped being able to
//  hear. Nothing throws, nothing logs, and on a clinical screen "no messages"
//  is an assertion of safety. So the outcome of the retry is PUBLISHED
//  (`degraded` / `error` / `failedChannelIds`) and a caller is expected to draw
//  a refusal, not an empty list, for a channel that appears in the failed set.
// ─────────────────────────────────────────────
import { useEffect, useMemo, useRef, useState } from "react";
import { listenToChannel } from "../firebase/firestore";
import { mergeChannelMessages } from "../domain/messaging";

// Retry budget. Unchanged from the original bounded retry — the fix surfaces
// the outcome, it does not make the hook try harder or give up sooner.
const MAX_RETRIES = 5;
const RETRY_MS = 2000;

// Stable identity for "nothing has failed", so the memos below don't hand out a
// fresh Set on every effect run.
const NO_FAILURES = {};

export function useChannelMessages(channelIds) {
  // Stable, deduped, sorted id list + a primitive join key for the effect deps.
  const ids = useMemo(() => {
    const unique = Array.from(new Set((channelIds || []).filter(Boolean)));
    unique.sort();
    return unique;
  }, [channelIds]);
  const idsKey = ids.join("|"); // primitive — safe in deps (no fresh-array churn)

  const [byChannel, setByChannel] = useState({});
  // Hold the latest map across snapshot callbacks without re-binding the effect.
  const mapRef = useRef({});

  // channelId → the error that ENDED that channel's retry budget. Only channels
  // that have given up entirely are in here; a retry still in flight is not a
  // failure (see the effect below).
  const [failures, setFailures] = useState(NO_FAILURES);
  const failRef = useRef(NO_FAILURES);

  useEffect(() => {
    // The whole listener set is torn down and re-armed whenever the id set
    // changes, so every channel gets a fresh retry budget — yesterday's verdict
    // must not stick to today's channels (their ids embed dateKey, so a day
    // rollover always lands here).
    if (failRef.current !== NO_FAILURES) {
      failRef.current = NO_FAILURES;
      setFailures(NO_FAILURES);
    }

    if (ids.length === 0) {
      mapRef.current = {};
      setByChannel({});
      return undefined;
    }

    // Prune any channels no longer subscribed (membership/day changed).
    const next = {};
    for (const id of ids) next[id] = mapRef.current[id] || [];
    mapRef.current = next;
    setByChannel(next);

    // Record / clear a channel's terminal failure. Kept ref-first so a snapshot
    // callback can update it without re-binding the effect, exactly like mapRef.
    const noteFailure = (id, err) => {
      if (failRef.current[id]) return; // first error wins; don't churn state
      failRef.current = {
        ...failRef.current,
        [id]: {
          channelId: id,
          code: err?.code || null,
          message: err?.message || "Channel messages could not be loaded.",
        },
      };
      setFailures(failRef.current);
    };
    const clearFailure = (id) => {
      if (!failRef.current[id]) return;
      const rest = { ...failRef.current };
      delete rest[id];
      failRef.current = Object.keys(rest).length ? rest : NO_FAILURES;
      setFailures(failRef.current);
    };

    // Per-channel subscription with bounded retry. A just-derived channel the
    // viewer can SEE may not yet have their uid in the MATERIALIZED memberIds
    // (materialization is debounced ~1.5s), so the channelMessages read rule
    // denies on first attach. Instead of leaving a dead listener (messages
    // invisible until a manual reload), we re-subscribe a few times — by then the
    // channel doc has been materialized with the viewer and the read succeeds.
    //
    // A retry IN FLIGHT is deliberately not "degraded": that first-attach race
    // is the normal cold start, and a warning every user sees on every launch is
    // a warning nobody reads when it finally means something. Only the exhausted
    // budget — the point at which this hook stops trying — is published.
    const cleanups = ids.map((id) => {
      let active = true;
      let attempt = 0;
      let unsub = null;
      let timer = null;
      const sub = () => {
        if (!active) return;
        unsub = listenToChannel(
          id,
          (rows) => {
            mapRef.current = { ...mapRef.current, [id]: rows };
            setByChannel(mapRef.current);
            clearFailure(id); // a channel that is delivering again is not dark
          },
          (err) => {
            if (!active) return;
            if (attempt >= MAX_RETRIES) {
              // Give up after ~10s — and SAY SO. This used to `return` in
              // silence, which is the whole defect: the caller was left holding
              // an empty array it could only read as "quiet".
              noteFailure(id, err);
              return;
            }
            attempt += 1;
            if (unsub) { try { unsub(); } catch { /* ignore */ } unsub = null; }
            timer = setTimeout(sub, RETRY_MS);
          }
        );
      };
      sub();
      return () => {
        active = false;
        if (timer) clearTimeout(timer);
        if (unsub) { try { unsub(); } catch { /* ignore */ } }
      };
    });

    return () => { for (const c of cleanups) c(); };
    // eslint-disable-next-line
  }, [idsKey]); // STABLE primitive key — not the fresh `channelIds` array (deps intentional).

  // Flattened, sorted, de-duplicated view for unread counting + a global feed.
  const all = useMemo(() => mergeChannelMessages(byChannel), [byChannel]);

  // Every channel whose listener has given up. A Set (mirrors AlertContext's
  // `emergencyRoomIds` / `breakRequestRoomIds`) so a per-room surface can ask
  // about ITS one channel in O(1): `failedChannelIds.has(channelId)`.
  const failedChannelIds = useMemo(() => new Set(Object.keys(failures)), [failures]);
  // One representative error, deterministic (lowest channel id) so it doesn't
  // flip between renders. `failedChannelIds` is the complete answer — a caller
  // must not read `error` as "exactly one channel is down".
  const error = useMemo(() => {
    const first = Object.keys(failures).sort()[0];
    return first ? failures[first] : null;
  }, [failures]);

  return {
    byChannel,
    all,
    // TRUE when at least one subscribed channel has stopped listening for good.
    // A caller must draw an explicit failure state for it — never an empty list,
    // and never a count of 0.
    degraded: failedChannelIds.size > 0,
    error,
    failedChannelIds,
  };
}
